import { useEffect, useState, useMemo } from "react";
import api, { downloadPdf } from "@/lib/api";
import { ShieldCheck, CheckCircle, UploadSimple, FilePdf, Warning, Trash, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DueDiligenceTab({ cid }) {
  const [dd, setDd] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = () => api.get(`/companies/${cid}/due-diligence`).then((r) => setDd(r.data));
  useEffect(() => { load(); }, [cid]);

  const toggle = async (item) => {
    const next = item.status === "done" ? "todo" : item.status === "todo" ? "progress" : "done";
    await api.patch(`/companies/${cid}/due-diligence`, { item_id: item.id, status: next });
    load();
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/companies/${cid}/due-diligence/analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) { console.debug("DD analyze failed for one file", e); /* continue */ }
    }
    e.target.value = "";
    if (ok) toast.success(`${ok} document(s) analysé(s) par l'IA`);
    else toast.error("Erreur d'analyse du/des document(s)");
    await load();
    setUploading(false);
  };

  const deleteAnalysis = async (id) => {
    await api.delete(`/companies/${cid}/due-diligence/analyses/${id}`);
    load();
  };

  const exportPdf = async () => {
    setExporting(true);
    try { await downloadPdf(`/companies/${cid}/due-diligence/pdf`, "due_diligence.pdf"); }
    catch { toast.error("Erreur export PDF"); } finally { setExporting(false); }
  };

  const grouped = useMemo(() => {
    if (!dd) return [];
    const m = new Map();
    for (const it of dd.items) {
      if (!m.has(it.category)) m.set(it.category, []);
      m.get(it.category).push(it);
    }
    return [...m.entries()];
  }, [dd]);

  if (!dd) return <div className="text-muted-foreground">Chargement…</div>;
  const statusColor = { done: "#059669", progress: "#D97706", todo: "#94A3B8" };
  const statusLabel = { done: "Validé", progress: "En cours", todo: "À faire" };
  const gs = dd.global_score || { score: 0, niveau: "faible", deal_breakers: 0, alertes: 0, risques: 0, documents: 0 };
  const gsTone = gs.niveau === "élevé" ? { txt: "text-red-700", bg: "bg-red-50 border-red-200", bar: "bg-red-500" }
    : gs.niveau === "modéré" ? { txt: "text-amber-700", bg: "bg-amber-50 border-amber-200", bar: "bg-amber-500" }
    : { txt: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", bar: "bg-emerald-500" };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {grouped.map(([cat, items]) => (
          <div key={cat} className="bg-white border border-border">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /><span className="font-head font-bold text-sm">{cat}</span></div>
            {items.map((item) => (
              <button key={item.id} onClick={() => toggle(item)} data-testid={`dd-item-${item.id}`}
                className="w-full flex items-center justify-between px-5 py-3 border-b border-border last:border-0 hover:bg-secondary text-left">
                <span className="text-sm flex items-center gap-2">
                  <CheckCircle size={18} weight={item.status === "done" ? "fill" : "regular"} style={{ color: statusColor[item.status] }} />
                  {item.label}
                </span>
                <span className="overline px-2 py-0.5" style={{ color: statusColor[item.status], background: statusColor[item.status] + "18" }}>{statusLabel[item.status]}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="space-y-6">
        <div className={`border p-5 ${gsTone.bg}`} data-testid="dd-global-score">
          <div className="flex items-center justify-between mb-2">
            <span className="overline">Score de risque global</span>
            <span className={`text-[11px] font-bold uppercase tracking-wide ${gsTone.txt}`}>{gs.niveau}</span>
          </div>
          <div className="flex items-end gap-2 mb-2">
            <span className={`font-mono text-3xl font-bold ${gsTone.txt}`}>{gs.score}</span>
            <span className="text-sm text-muted-foreground mb-1">/100</span>
          </div>
          <div className="h-2 bg-white/70 rounded-full overflow-hidden mb-3">
            <div className={`h-full ${gsTone.bar}`} style={{ width: `${gs.score}%` }} />
          </div>
          <div className="text-xs text-muted-foreground">
            {gs.documents} doc. analysé(s) · {gs.deal_breakers} deal breaker(s) · {gs.alertes} alerte(s) · {gs.risques} risque(s)
          </div>
          <button data-testid="dd-export-pdf-btn" onClick={exportPdf} disabled={exporting || gs.documents === 0}
            className="w-full mt-3 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold bg-foreground text-white hover:opacity-90 disabled:opacity-50">
            <FilePdf size={15} /> {exporting ? "Export…" : "Exporter le rapport DD (PDF)"}
          </button>
        </div>

        <div className="ai-surface p-5">
          <div className="flex items-center gap-2 mb-3"><Sparkle size={16} weight="fill" className="text-[#6366f1]" /><span className="overline text-[#4f46e5]">IA Due Diligence</span></div>
          <p className="text-xs text-muted-foreground mb-3">Uploadez un ou plusieurs PDF (bilan, statuts, portefeuille). L'IA détecte les risques.</p>
          <label data-testid="dd-upload-label" className="flex items-center justify-center gap-2 border-2 border-dashed border-[#c7d2fe] py-6 cursor-pointer hover:bg-white/50 text-sm text-[#4f46e5] font-medium">
            <UploadSimple size={18} /> {uploading ? "Analyse en cours…" : "Choisir un ou plusieurs documents"}
            <input data-testid="dd-upload-input" type="file" accept=".pdf,.txt" multiple onChange={upload} className="hidden" disabled={uploading} />
          </label>
        </div>

        {(dd.analyses || []).map((a) => (
          <div key={a.id} className="bg-white border border-border p-5" data-testid="dd-analysis">
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-muted-foreground truncate">{a.filename}</div>
              <button data-testid={`dd-delete-${a.id}`} onClick={() => deleteAnalysis(a.id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash size={14} /></button>
            </div>
            {[["deal_breakers", "Deal breakers", "#DC2626"], ["risques_detectes", "Risques détectés", "#D97706"], ["alertes", "Alertes", "#002FA7"], ["questions_a_poser", "Questions à poser", "#475569"]].map(([k, label, col]) => (
              (a.analysis[k] || []).length > 0 && (
                <div key={k} className="mb-3">
                  <div className="overline mb-1 flex items-center gap-1" style={{ color: col }}><Warning size={12} weight="fill" />{label}</div>
                  <ul className="space-y-1">{a.analysis[k].map((x, i) => <li key={i} className="text-xs flex gap-1.5"><span style={{ color: col }}>•</span>{x}</li>)}</ul>
                </div>
              )
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
